import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import appConfig from '../../consumingApi/services/appConfig';
import LogisticService from '@LogisticService';

const BASE_URL = appConfig.logisticsUrl;
const REDUCER_NAME = 'adminStoneEntregaSlice';
const GET_CONSULTA = 'adminStoneEntregaSlice/getConsulta';
const REFRESH_DELIVERY = 'adminStoneEntregaSlice/refreshDelivery';
const POST_CANCELAMENTO = 'adminStoneEntregaSlice/postCancelamento';
const GET_ARQUIVOS = 'adminStoneEntregaSlice/getArquivos';

// Limite de arquivos por requisição aceito pelo back (MAX_ARQUIVOS_POR_REQUISICAO)
const ARQUIVOS_POR_LOTE = 30;

const EMPTY_CONSULTA = { total: 0, limit: 0, offset: 0, has_more: false, deliveries: [] };

const initialState = {
    consultaData: EMPTY_CONSULTA,
    consultaStatus: 'idle',
    consultaError: null,

    cancelamentoStatus: 'idle',
    cancelamentoError: null,

    // { [file_hash]: data-URI } das fotos/assinaturas das evidências abertas no drawer
    arquivos: {},
    arquivosStatus: 'idle',
};

const buildConsultaUrl = ({ filtro, valor, limit, offset }) => {
    const params = new URLSearchParams();
    params.append(filtro, valor);
    if (limit !== undefined) params.append('limit', limit);
    if (offset !== undefined) params.append('offset', offset);
    return `${BASE_URL}/ferramentas/admin-stone-entrega/consulta?${params.toString()}`;
};

// filtro: 'delivery_id' | 'tracking_code' | 'barcode' | 'polo' (exatamente um por vez)
export const getAdminStoneEntregaConsulta = createAsyncThunk(
    GET_CONSULTA,
    async ({ filtro, valor, limit, offset }, { rejectWithValue }) => {
        try {
            const res = await LogisticService.requestWithAuth(
                buildConsultaUrl({ filtro, valor, limit, offset })
            );
            return res.data;
        } catch (err) {
            // 404 = nenhuma delivery para o filtro; não é erro de tela, é resultado vazio
            if (err.response?.status === 404) return EMPTY_CONSULTA;
            return rejectWithValue(err.response?.data || err.message || 'Erro desconhecido');
        }
    }
);

// Rebusca uma delivery pelo id para atualizar a linha/drawer sem perder a listagem atual
export const refreshAdminStoneEntregaDelivery = createAsyncThunk(
    REFRESH_DELIVERY,
    async (deliveryId, { rejectWithValue }) => {
        try {
            const res = await LogisticService.requestWithAuth(
                buildConsultaUrl({ filtro: 'delivery_id', valor: deliveryId })
            );
            return res.data?.deliveries?.[0] || null;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message || 'Erro desconhecido');
        }
    }
);

// Fotos/assinaturas das evidências: o front não chama a deliveries-api direto porque
// GET /files/{hash} exige o header token, então o back repassa e devolve data-URIs.
export const getAdminStoneEntregaArquivos = createAsyncThunk(
    GET_ARQUIVOS,
    async (hashes, { rejectWithValue }) => {
        const lotes = [];
        for (let i = 0; i < hashes.length; i += ARQUIVOS_POR_LOTE) {
            lotes.push(hashes.slice(i, i + ARQUIVOS_POR_LOTE));
        }
        try {
            const respostas = await Promise.all(
                lotes.map((lote) =>
                    LogisticService.requestWithAuth(
                        `${BASE_URL}/ferramentas/admin-stone-entrega/arquivos?hashes=${lote
                            .map(encodeURIComponent)
                            .join(',')}`
                    )
                )
            );
            return Object.assign({}, ...respostas.map((res) => res.data?.arquivos || {}));
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message || 'Erro desconhecido');
        }
    }
);

export const postAdminStoneEntregaCancelamento = createAsyncThunk(
    POST_CANCELAMENTO,
    async ({ deliveryId, motivo }, { dispatch, rejectWithValue }) => {
        try {
            const res = await LogisticService.postWithAuth(
                `${BASE_URL}/ferramentas/admin-stone-entrega/cancelamento`,
                { delivery_id: deliveryId, motivo }
            );
            dispatch(refreshAdminStoneEntregaDelivery(deliveryId));
            return res.data;
        } catch (err) {
            return rejectWithValue({
                status: err.response?.status,
                data: err.response?.data || err.message || 'Erro desconhecido',
            });
        }
    }
);

export const adminStoneEntregaSlice = createSlice({
    name: REDUCER_NAME,
    initialState,
    reducers: {
        resetAdminStoneEntregaConsulta: (state) => {
            state.consultaData = EMPTY_CONSULTA;
            state.consultaStatus = 'idle';
            state.consultaError = null;
        },
        resetAdminStoneEntregaCancelamento: (state) => {
            state.cancelamentoStatus = 'idle';
            state.cancelamentoError = null;
        },
        // imagens em base64 pesam: descarta ao fechar o drawer
        resetAdminStoneEntregaArquivos: (state) => {
            state.arquivos = {};
            state.arquivosStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAdminStoneEntregaConsulta.pending, (state) => {
                state.consultaStatus = 'pending';
                state.consultaError = null;
            })
            .addCase(getAdminStoneEntregaConsulta.fulfilled, (state, action) => {
                state.consultaData = action.payload;
                state.consultaStatus = 'fulfilled';
            })
            .addCase(getAdminStoneEntregaConsulta.rejected, (state, action) => {
                state.consultaData = EMPTY_CONSULTA;
                state.consultaError = action.payload;
                state.consultaStatus = 'rejected';
            })

            .addCase(refreshAdminStoneEntregaDelivery.fulfilled, (state, action) => {
                const updated = action.payload;
                if (!updated) return;
                state.consultaData.deliveries = state.consultaData.deliveries.map((d) =>
                    d.id === updated.id ? updated : d
                );
            })

            .addCase(getAdminStoneEntregaArquivos.pending, (state) => {
                state.arquivosStatus = 'pending';
            })
            .addCase(getAdminStoneEntregaArquivos.fulfilled, (state, action) => {
                state.arquivos = { ...state.arquivos, ...action.payload };
                state.arquivosStatus = 'fulfilled';
            })
            .addCase(getAdminStoneEntregaArquivos.rejected, (state) => {
                state.arquivosStatus = 'rejected';
            })

            .addCase(postAdminStoneEntregaCancelamento.pending, (state) => {
                state.cancelamentoStatus = 'pending';
                state.cancelamentoError = null;
            })
            .addCase(postAdminStoneEntregaCancelamento.fulfilled, (state) => {
                state.cancelamentoStatus = 'fulfilled';
            })
            .addCase(postAdminStoneEntregaCancelamento.rejected, (state, action) => {
                state.cancelamentoError = action.payload;
                state.cancelamentoStatus = 'rejected';
            });
    },
});

export const {
    resetAdminStoneEntregaConsulta,
    resetAdminStoneEntregaCancelamento,
    resetAdminStoneEntregaArquivos,
} = adminStoneEntregaSlice.actions;

export const adminStoneEntregaSelector = (state) => state.adminStoneEntregaSlice;

export default adminStoneEntregaSlice.reducer;
